import type { CastProfile } from './castProfiles.ts'
import type { CastProfileTimeline } from './castProfileTimelines.ts'

// Individually sourced newcomer backgrounds, reviewed October 1, 2026.
// Creator-owned show links resolve the earlier same-name identity holdouts.
// Historical college records and fashion promotions do not certify current
// jobs, event attendance, dating outcomes or individual episode appearances.
export const finalHuntProfilesCT: CastProfile[] = [
  {
    personId: 'p-chad-90-day-hunt-for-love-s1',
    headline: 'A Georgia football alum tries a new playing field, where the introductions happen poolside.',
    bio: [
      'Before his reality-TV introduction, Chad Gloer played football at the University of Georgia. The Bulldogs’ 2010 report records his journey from walk-on to scholarship recipient, with special-teams recognition along the way; the university’s 2011 graduation announcement lists his studies in risk management and insurance. His pre-TV résumé already knew a thing or two about a long game.',
      'Chad later joined Hunt for Love’s first-season retreat as one of the new singles, sharing his own TLC casting announcement in May 2025. His public profile identifies him with Atlanta and UGA, but his franchise entrance was an independent dating introduction—not the continuation of an established 90 Day couple. Tulum supplied the new setting; a verified lasting match remains a separate question.',
    ],
    originStory: 'TLC introduced Chad among the new singles joining Hunt for Love Season 1 in Tulum. His creator account’s May 2025 casting poster confirms that entry; he was not introduced through a previously featured franchise partner.',
    journeys: [
      {
        label: 'Where it started: a new single in Tulum',
        era: 'Hunt for Love S1',
        summary: 'Chad entered the singles retreat independently. The checked sources establish his introduction and earlier Georgia background, not a confirmed original couple or present relationship outcome.',
      },
    ],
    sources: [
      {
        label: 'Chad’s public creator profile and casting announcement',
        url: 'https://www.instagram.com/chadgloer/',
      },
      {
        label: 'University of Georgia: Chad’s 2010 football scholarship',
        url: 'https://georgiadogs.com/news/2010/5/12/Chad_Gloer_Awarded_UGA_Football_Scholarship',
      },
      {
        label: 'University of Georgia: 2011 student-athlete graduation announcement',
        url: 'https://georgiadogs.com/news/2011/5/11/Sixty_Nine_UGA_Student_Athletes_to_Receive_Degrees_on_Friday',
      },
      {
        label: 'TLC: Chad among the new retreat singles',
        url: 'https://www.tlc.com/shows/90-day-hunt-for-love/meet-the-90-day-hunt-for-love-singles-retreat-hopefuls',
      },
      {
        label: 'Us Weekly: Hunt for Love’s Tulum retreat format',
        url: 'https://www.usmagazine.com/entertainment/news/90-day-hunt-for-love-sneak-peek-rob-finds-love-at-first-sight/',
      },
    ],
  },
  {
    personId: 'p-tessa-90-day-hunt-for-love-s1',
    headline: 'A New York model enters the franchise with a fresh chapter—and a different kind of casting call.',
    bio: [
      'Tessa Victoria describes herself as a New York model. Her own public link hub pairs fashion promotions with her television introduction, including a July 2025 fashion showcase and a September 2025 New York Fashion Week Festival link bearing her name. Those are her shared promotions, not proof she walked a particular runway; the wardrobe résumé does not need invented accessories.',
      'That same creator-owned hub directs visitors to watch Tessa on Hunt for Love, confirming that the model and the retreat newcomer are the same person. TLC introduced her among the first season’s new singles in Tulum, rather than as a returning alum’s established partner. It was a first franchise chapter of her own, with no verified lasting match or current partner attached in the checked sources.',
    ],
    originStory: 'Tessa joined Hunt for Love Season 1 independently as a new retreat single. Her creator profile links directly to her own hub, which explicitly points viewers to her appearance on the show; no earlier franchise couple is assigned to her introduction.',
    journeys: [
      {
        label: 'Where it started: joining the new singles',
        era: 'Hunt for Love S1',
        summary: 'Tessa entered the franchise as a new participant at the Tulum singles retreat. Tentative third-party speculation about a match is not treated as a confirmed relationship or current status.',
      },
    ],
    sources: [
      {
        label: 'Tessa’s public creator profile: New York model',
        url: 'https://www.instagram.com/tessavictoriaaa/',
      },
      {
        label: 'Tessa’s own links: Hunt for Love and fashion promotions',
        url: 'https://link.me/tessavictoriaaa',
      },
      {
        label: 'TLC: Tessa among the new retreat singles',
        url: 'https://www.tlc.com/shows/90-day-hunt-for-love/meet-the-90-day-hunt-for-love-singles-retreat-hopefuls',
      },
      {
        label: 'Us Weekly: Hunt for Love’s Tulum retreat format',
        url: 'https://www.usmagazine.com/entertainment/news/90-day-hunt-for-love-sneak-peek-rob-finds-love-at-first-sight/',
      },
    ],
  },
]

export const finalHuntTimelinesCT: Record<string, CastProfileTimeline> = {
  'p-chad-90-day-hunt-for-love-s1': {
    origin: {
      partnerName: 'Joining the retreat independently',
      introLabel: 'A new single in Tulum',
      show: '90 Day: Hunt for Love',
      season: 1,
      connectionKind: 'independent',
    },
    connections: [],
    seasons: [
      {
        show: '90 Day: Hunt for Love',
        season: 1,
        title: 'From a Georgia background to a Tulum introduction',
        recap: 'The Georgia football alum joined the new singles at the Tulum retreat and shared his own May 2025 TLC casting poster. This season introduced him independently; a specific lasting match and individual episode appearances remain unverified.',
        status: 'aired',
      },
    ],
  },
  'p-tessa-90-day-hunt-for-love-s1': {
    origin: {
      partnerName: 'Joining the retreat independently',
      introLabel: 'A new single in Tulum',
      show: '90 Day: Hunt for Love',
      season: 1,
      connectionKind: 'independent',
    },
    connections: [],
    seasons: [
      {
        show: '90 Day: Hunt for Love',
        season: 1,
        title: 'A New York model’s first franchise chapter',
        recap: 'Tessa joined TLC’s new retreat singles, with her own creator links confirming the New York model’s Hunt for Love appearance. A confirmed romance or post-retreat relationship outcome is not established by those sources.',
        status: 'aired',
      },
    ],
  },
}
